<template>
  <div class="alerts-table-inner-wrapper responsive-table-wrapper">
    <v-data-table
      :headers="headers"
      :items="filteredAlerts"
      v-model:items-per-page="itemsPerPage"
      class="elevation-0 mt-2 pretty-table"
      item-key="identifier"
      @click:row="goToDetails"
      :search="search"
      :loading="loading"
      :loading-text="$t('loadingAlerts')"
      :style="{
        direction: $i18n.locale === 'he' ? 'rtl' : 'ltr',
        width: '100%',
        maxWidth: '100%',
        tableLayout: 'fixed',
      }"
      density="comfortable"
      hover
      rounded
      color="surface"
      v-model:sort-by="sortBy"
      fixed-header
      height="400"
    >
      <template #headers="{ columns, toggleSort, isSorted, getSortIcon }">
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            class="text-center"
            :style="{
              width: column.width,
              minWidth: column.width,
              maxWidth: column.width,
              cursor: column.sortable !== false ? 'pointer' : 'default',
            }"
            @click="() => column.sortable !== false && toggleSort(column)"
          >
            <v-tooltip :text="column.tooltip" location="top">
              <template #activator="{ props }">
                <div
                  v-bind="props"
                  class="d-flex align-center justify-center header-cell"
                >
                  <span class="header-text">
                    {{ column.title }}
                  </span>
                  <v-icon
                    v-if="isSorted(column)"
                    size="16"
                    class="ml-1"
                    :icon="getSortIcon(column)"
                  />
                </div>
              </template>
            </v-tooltip>
          </th>
        </tr>
      </template>

      <!-- Custom cell templates -->
      <template #item.identifier="{ item }">
        <v-tooltip :text="item.identifier || '0000'" location="top">
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 40px; display: block"
            >
              {{ getLastFourDigits(item.identifier) }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.sender="{ item }">
        <v-tooltip :text="item.sender || '-'" location="top">
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 60px; display: block"
            >
              {{ item.sender || "-" }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.sent="{ item }">
        <v-tooltip
          :text="
            item.sent
              ? new Date(item.sent).toLocaleString(
                  $i18n.locale === 'he' ? 'he-IL' : 'en-US'
                )
              : '-'
          "
          location="top"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 90px; display: block"
            >
              {{
                item.sent
                  ? new Date(item.sent).toLocaleString(
                      $i18n.locale === "he" ? "he-IL" : "en-US"
                    )
                  : "-"
              }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.msgType="{ item }">
        <v-tooltip :text="item.msgType || $t('unknown')" location="top">
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 50px; display: block"
            >
              {{ item.msgType || $t("unknown") }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.status="{ item }">
        <v-chip :color="getStatusColor(item.status)" dark size="x-small">
          {{ $t(item.status ? item.status.toLowerCase() : "unknown") }}
        </v-chip>
      </template>
      <template #item.headline="{ item }">
        <v-tooltip :text="getHeadline(item) || '-'" location="top">
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 110px; display: block"
            >
              {{ getHeadline(item) || "-" }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.messageSize="{ item }">
        <span
          class="text-truncate ellipsis-cell d-flex justify-center"
          style="max-width: 50px; display: block"
        >
          {{ formatBytes(item.messageSize || 0) }}
        </span>
      </template>
      <template #item.layers="{ item }">
        <v-tooltip
          :text="
            item.layers && item.layers.length ? item.layers.join(', ') : '-'
          "
          location="top"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="text-truncate ellipsis-cell d-flex justify-center"
              style="max-width: 60px; display: block"
            >
              {{
                item.layers && item.layers.length ? item.layers.join(", ") : "-"
              }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template #item.severity="{ item }">
        <v-chip
          :color="getSeverityColor(item.severity)"
          size="x-small"
          variant="flat"
        >
          {{ $t(item.severity ? item.severity.toLowerCase() : "unknown") }}
        </v-chip>
      </template>

      <template #no-data>
        <v-row justify="center" align="center" class="pa-8">
          <v-col cols="12" class="text-center">
            <v-icon size="48" color="info">mdi-bell-off-outline</v-icon>
            <div class="mt-2">{{ $t("noAlerts") }}</div>
          </v-col>
        </v-row>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { useRouter } from "vue-router";
import { getSeverityColor } from "../../config/severityColors.js";
import { getStatusColor } from "../../config/statusColors.js";
import { itemsPerPage, sortBy } from "../../composables/useTablePrefs.js";

export default {
  name: "AlertsDataTable",
  props: {
    headers: {
      type: Array,
      required: true,
    },
    filteredAlerts: {
      type: Array,
      required: true,
    },
    search: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["row-click"],
  setup() {
    const router = useRouter();
    return { router, itemsPerPage, sortBy };
  },
  computed: {
    ...mapGetters(["alerts"]),
    currentLang() {
      return this.$i18n.locale || "en";
    },
  },
  methods: {
    goToDetails(_, row) {
      const alert = row.item;
      if (alert && alert.identifier) {
        this.router.push({
          name: "AlertDetails",
          params: { identifier: alert.identifier },
        });
      } else {
        console.error("No alert or alert identifier found:", alert);
      }
    },
    formatBytes(bytes) {
      if (bytes === 0) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    },
    getSeverityColor,
    getStatusColor,
    getLastFourDigits(id) {
      if (!id) return "####";
      return id.toString().slice(-4) || "0000";
    },
    getHeadline(item) {
      if (!item.headlines) return null;
      return (
        item.headlines[this.currentLang] ||
        Object.values(item.headlines)[0] ||
        null
      );
    },
    getDescription(item) {
      if (!item.descriptions) return null;
      return (
        item.descriptions[this.currentLang] ||
        Object.values(item.descriptions)[0] ||
        null
      );
    },
  },
};
</script>

<style scoped>
.alerts-table-inner-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.pretty-table {
  border-radius: 0 0 18px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  font-size: 0.9rem;
  background: none;
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  overflow-x: hidden;
}

.v-data-table {
  background: none;
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  font-size: 0.85rem;
  overflow-x: hidden;
}

.v-data-table__wrapper {
  overflow-x: hidden !important;
  max-width: 100%;
}

.v-data-table__wrapper table {
  width: 100%;
  table-layout: fixed;
  min-width: 100%;
  max-width: 100%;
}

.v-data-table th,
.v-data-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 2px;
  font-size: 0.8rem;
}

.v-data-table tbody tr {
  transition: background 0.2s;
}

.v-data-table tbody tr:hover {
  background: var(--v-theme-secondary) !important;
  cursor: pointer;
  color: var(--v-theme-on-secondary);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* This class forces ellipsis and prevents content from pushing other columns */
.ellipsis-cell {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.v-data-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 0;
  padding: 8px 4px;
}

.v-data-table th {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 12px 4px;
  background: var(--v-theme-surface-variant);
  font-weight: 600;
  color: var(--v-theme-primary);
  font-size: 0.75rem;
}

.v-chip {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.7rem;
  height: 20px;
}

.keyword-chip {
  font-size: 0.65rem;
  height: 18px;
  flex-shrink: 0;
}

.header-cell {
  background: var(--v-theme-surface-variant);
  border-radius: 6px;
  padding: 4px;
  margin: 1px;
}

.header-text {
  color: var(--v-theme-primary);
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: none;
  letter-spacing: 0.5px;
}

.responsive-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

@media (max-width: 768px) {
  .responsive-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 1em;
  }
  .pretty-table {
    font-size: 0.95em;
  }
  .header-cell,
  .v-data-table__td {
    padding: 0.5em 0.3em;
    min-width: 80px;
  }
  .v-data-table__wrapper {
    min-width: 600px;
  }
}
</style>
